import { formatTime } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { post } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics";
import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";

import { useCurrentUserId } from "../../lib/useCurrentUserId";
import { useIsAdmin } from "../../lib/useIsAdmin";
import { useLogHomeHealthEvent } from "../../lib/useLogHomeHealthEvent";
import { useInvalidateHomeHealthListQuery } from "../HomeHealthListRefetchGuard";
import {
  type CreateVisitRequest,
  createVisitRequestSchema,
  type ResponseWithError,
  type Visit,
  visitResponseSchema,
} from "../types";
import { getCaseVisitsQueryKey } from "./useCaseVisits";
import { getVisitQueryKey } from "./useVisit";

// Input type that expects Date instead of string for estimatedStartTime
type CreateVisitInput = Omit<CreateVisitRequest, "data"> & {
  data: Omit<CreateVisitRequest["data"], "attributes"> & {
    attributes: Omit<CreateVisitRequest["data"]["attributes"], "estimatedStartTime"> & {
      estimatedStartTime?: Date | null;
    };
  };
};

export function useCreateVisit(
  options: UseMutationOptions<Visit, AxiosError<ResponseWithError>, CreateVisitInput> = {}
) {
  const queryClient = useQueryClient();
  const invalidateListQuery = useInvalidateHomeHealthListQuery();
  const workplaceId = useCurrentUserId() ?? "";
  const isAdmin = useIsAdmin();
  const logHomeHealthEvent = useLogHomeHealthEvent();

  return useMutation({
    mutationFn: async (params) => {
      const { estimatedStartTime, ...restAttributes } = params.data.attributes;
      const apiParams: CreateVisitRequest = {
        ...params,
        data: {
          ...params.data,
          attributes: {
            ...restAttributes,
            ...(isDefined(estimatedStartTime)
              ? { estimatedStartTime: formatTime(estimatedStartTime) }
              : {}),
          },
        },
      };
      const response = await post({
        url: `${environmentConfig.REACT_APP_HOME_HEALTH_API_URL}/v1/${workplaceId}/visits`,
        data: apiParams,
        requestSchema: createVisitRequestSchema,
        responseSchema: visitResponseSchema,
      });
      return response.data.data;
    },
    onSuccess: (newVisit) => {
      logHomeHealthEvent(APP_V2_USER_EVENTS.HOME_HEALTH_VISIT_CREATED, {
        workplaceId,
        type: newVisit.attributes.type,
        workerReq: newVisit.attributes.workerReq,
        caseId: newVisit.attributes.caseId,
        visitId: newVisit.id,
        performedByAdmin: isAdmin,
      });
      queryClient.setQueryData(
        getCaseVisitsQueryKey({ workplaceId, caseId: newVisit.attributes.caseId }),
        // eslint-disable-next-line unicorn/prefer-spread
        (oldVisits?: Visit[]) => (oldVisits ?? []).concat(newVisit)
      );
      invalidateListQuery(
        getCaseVisitsQueryKey({ workplaceId, caseId: newVisit.attributes.caseId })
      );
      queryClient.setQueryData(getVisitQueryKey({ workplaceId, visitId: newVisit.id }), newVisit);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      queryClient.invalidateQueries(getVisitQueryKey({ workplaceId, visitId: newVisit.id }));
    },
    ...options,
  });
}
